import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Attributes (`attribute`, `attributeValue`, CONTEXT.md) in the admin: properties the shop filters
 * on, each with fixed values (formaat: 28×22 cm, 46×27 cm; soort: wegwerp, wasbaar). At most one
 * attribute is the size (`isSize`): its value is the first half of a variant's size label. Values
 * are given to products and variants in the product editor. Every write answers the whole list:
 * the attributes in order, each with its values in order.
 */

export const AttributeValue = z.object({
	id: z.string(),
	name: z.string(),
	/** 0 is first within the attribute. */
	position: z.number().int(),
	/** How many products and variants hold this value. */
	usageCount: z.number().int(),
});
export type AttributeValue = z.infer<typeof AttributeValue>;

export const Attribute = z.object({
	id: z.string(),
	name: z.string(),
	position: z.number().int(),
	/** Its value is a variant's size in the size label. At most one attribute is. */
	isSize: z.boolean(),
	values: z.array(AttributeValue),
});
export type Attribute = z.infer<typeof Attribute>;

/**
 * An attribute's fields. `isSize: true` makes this the size attribute; the one that was is no
 * longer. A new attribute goes to the end of the list.
 */
export const AttributeFields = z.object({
	name: z.string().trim().min(1).max(100),
	isSize: z.boolean().default(false),
});
export type AttributeFields = z.input<typeof AttributeFields>;

/** A value's name: `46×27 cm`, `wegwerp`. A new value goes to the end of its attribute. */
export const AttributeValueFields = z.object({ name: z.string().trim().min(1).max(100) });
export type AttributeValueFields = z.infer<typeof AttributeValueFields>;

/** A new order: every id, each once, first to last. */
export const AttributeOrder = z.object({
	ids: z
		.array(z.string())
		.max(1000)
		.refine((ids) => new Set(ids).size === ids.length, "each id once"),
});

const AttributePath = z.object({ attributeId: z.string().min(1) });
const ValuePath = AttributePath.extend({ valueId: z.string().min(1) });
const List = z.array(Attribute);

export const AttributeContract = {
	list: oc.route({ method: "GET", path: "/admin/attributes" }).output(List),
	/** Errors: `AttributeNameTaken` (409). */
	create: oc.route({ method: "POST", path: "/admin/attributes" }).input(AttributeFields).output(List),
	/** Errors: `AttributeNotFound` (404), `AttributeNameTaken` (409). */
	update: oc
		.route({ method: "PUT", path: "/admin/attributes/{attributeId}" })
		.input(AttributeFields.extend(AttributePath.shape))
		.output(List),
	/**
	 * Deletes the attribute with its values, and takes those values off every product and variant,
	 * in one batch. Errors: `AttributeNotFound` (404).
	 */
	delete: oc
		.route({ method: "DELETE", path: "/admin/attributes/{attributeId}" })
		.input(AttributePath)
		.output(List),
	/** Errors: `AttributeOrderOutdated` (409): the order does not name every attribute exactly once. */
	reorder: oc.route({ method: "PUT", path: "/admin/attributes/order" }).input(AttributeOrder).output(List),

	/** Errors: `AttributeNotFound` (404), `AttributeValueNameTaken` (409). */
	addValue: oc
		.route({ method: "POST", path: "/admin/attributes/{attributeId}/values" })
		.input(AttributeValueFields.extend(AttributePath.shape))
		.output(List),
	/** Errors: `AttributeValueNotFound` (404), `AttributeValueNameTaken` (409). */
	updateValue: oc
		.route({ method: "PUT", path: "/admin/attributes/{attributeId}/values/{valueId}" })
		.input(AttributeValueFields.extend(ValuePath.shape))
		.output(List),
	/** Deletes the value and takes it off every product and variant, in one batch. Errors: `AttributeValueNotFound` (404). */
	deleteValue: oc
		.route({ method: "DELETE", path: "/admin/attributes/{attributeId}/values/{valueId}" })
		.input(ValuePath)
		.output(List),
	/** Errors: `AttributeNotFound` (404), `AttributeOrderOutdated` (409): not exactly the attribute's values. */
	reorderValues: oc
		.route({ method: "PUT", path: "/admin/attributes/{attributeId}/values/order" })
		.input(AttributeOrder.extend(AttributePath.shape))
		.output(List),
};
